import { useState, useEffect, useMemo } from 'react';
import { Select, MenuItem, FormControl, InputLabel, SelectChangeEvent, Typography } from '@mui/material';
import { useOrgUnits } from '@/hooks/core/useOrgUnit';
import { useMyMemberships } from '@/hooks/core/useOrgUnitMember.custom';
import { authService } from '@/services/auth/authService';
import { useTranslation } from 'react-i18next';

/**
 * Legal Entity Selector Component
 *
 * Displays a dropdown in the app header for users to select their current Legal Entity.
 * The selected Legal Entity is sent to the backend via X-Nexo-Legal-Entity-Id header
 * and used for scope derivation when creating entities.
 *
 * When the user has OrgUnitMember records, the dropdown is filtered to only show
 * Legal Entities where the user has active membership. Falls back to showing all
 * Legal Entities if no memberships are loaded (backward compatible).
 *
 * See: docs/15-designspecs/nexo_event_scope_specification_v_1.md (Scope Derivation v1 Rules)
 */
export function LegalEntitySelector() {
  const { t } = useTranslation();
  const { data: orgUnits, isLoading: orgUnitsLoading } = useOrgUnits();
  const { data: myMemberships, isLoading: membershipsLoading } = useMyMemberships();
  const isLoading = orgUnitsLoading || membershipsLoading;
  const [selectedId, setSelectedId] = useState<string>(
    authService.getLegalEntityId() || ''
  );

  // Filter to only Legal Entity type OrgUnits the user has membership for.
  // Fallback: if no memberships loaded or user has none, show all Legal Entities.
  const legalEntities = useMemo(() => {
    const allLegalEntities = orgUnits?.filter(
      (ou) => ou.type === 'LEGAL_ENTITY' && ou.isActive
    ) || [];

    if (!myMemberships || myMemberships.length === 0) {
      return allLegalEntities;
    }

    const memberOrgUnitIds = new Set(myMemberships.map((m) => m.orgUnitId));
    const filtered = allLegalEntities.filter((le) => memberOrgUnitIds.has(le.id));

    // If filtering results in zero matches (e.g., user is member of sub-units only),
    // fall back to all Legal Entities to avoid empty dropdown
    return filtered.length > 0 ? filtered : allLegalEntities;
  }, [orgUnits, myMemberships]);

  const handleChange = (event: SelectChangeEvent<string>) => {
    const id = event.target.value;
    const selected = legalEntities.find((le) => le.id === id);
    setSelectedId(id);
    authService.setLegalEntityId(id || null);
    authService.setLegalEntityName(selected?.name || null);
  };

  // Validate saved LE is still in the user's available list, auto-select if only one exists
  useEffect(() => {
    if (legalEntities.length === 0) return;

    // If saved LE is valid, keep it
    if (selectedId && legalEntities.some((le) => le.id === selectedId)) {
      return;
    }

    // Saved LE is invalid or missing — auto-select if only one exists
    if (legalEntities.length === 1) {
      const firstEntity = legalEntities[0];
      setSelectedId(firstEntity.id);
      authService.setLegalEntityId(firstEntity.id);
      authService.setLegalEntityName(firstEntity.name);
    } else if (selectedId) {
      // Saved LE no longer available (membership changed) — clear it
      setSelectedId('');
      authService.setLegalEntityId(null);
      authService.setLegalEntityName(null);
    }
  }, [legalEntities, selectedId]);

  // Show loading state
  if (isLoading) {
    return (
      <FormControl size="small" sx={{ minWidth: 80, maxWidth: 120 }}>
        <InputLabel>{t('common.loading')}</InputLabel>
      </FormControl>
    );
  }

  // Show message if no legal entities configured
  if (legalEntities.length === 0) {
    return (
      <Typography
        variant="body2"
        sx={{
          color: 'rgba(255, 255, 255, 0.7)',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {t('legalEntity.noEntities')}
      </Typography>
    );
  }

  return (
    <FormControl
      size="small"
      sx={{
        minWidth: 90,
        maxWidth: 120,
        mx: 0.5,
        '& .MuiOutlinedInput-root': {
          color: 'inherit',
          '& fieldset': {
            borderColor: 'rgba(255, 255, 255, 0.5)',
          },
          '&:hover fieldset': {
            borderColor: 'rgba(255, 255, 255, 0.7)',
          },
          '&.Mui-focused fieldset': {
            borderColor: 'white',
          },
        },
        '& .MuiInputLabel-root': {
          color: 'rgba(255, 255, 255, 0.7)',
          '&.Mui-focused': {
            color: 'white',
          },
        },
        '& .MuiSelect-icon': {
          color: 'rgba(255, 255, 255, 0.7)',
        },
      }}
    >
      <InputLabel id="legal-entity-label">{t('legalEntity.label')}</InputLabel>
      <Select
        labelId="legal-entity-label"
        id="legal-entity-select"
        value={selectedId}
        label={t('legalEntity.label')}
        onChange={handleChange}
      >
        {legalEntities.map((le) => (
          <MenuItem key={le.id} value={le.id}>
            {le.name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}
